import * as THREE from 'three'
import { gsap } from 'gsap'
import Experience from '../../../Experience'
import vertexShader from '../Shader/moonRays/vertex.glsl'
import fragmentShader from '../Shader/moonRays/fragment.glsl'
import flareVertexShader from '../Shader/lensFlare/vertex.glsl'
import flareFragmentShader from '../Shader/lensFlare/fragment.glsl'

/**
 * MoonRays — 月がトリガーゾーンに入った時のエフェクト2層。
 *  1. アナモルフィック光条: 横に長く伸びる細いストリーク＋十字のコア
 *     （天体のワールド座標に追従するビルボード）
 *  2. レンズフレア: 「月 → 画面中心 → その先」の軸上に並ぶゴースト円盤とリング
 *     （フルスクリーンのスクリーンスペースシェーダー。カメラを動かすと軸に沿って滑る）
 * 各1ドローコール。
 *
 * エフェクト共通インターフェース: fadeIn / fadeOut / updatePosition / update / dispose
 */
export default class MoonRays
{
  constructor(bodyConfig)
  {
    this.experience = new Experience()
    this.scene = this.experience.scene
    this.camera = this.experience.camera
    this.sizes = this.experience.sizes
    this.quality = this.experience.quality

    this.worldPosition = new THREE.Vector3()
    this.scratchVec3 = new THREE.Vector3()

    // カメラから遠い（〜50ユニット）ので画面幅いっぱいに伸びるよう大きめに
    const geometry = new THREE.PlaneGeometry(44, 10)

    this.material = new THREE.ShaderMaterial({
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      uniforms: {
        uColor: new THREE.Uniform(new THREE.Color('#fff4e0')),
        uEdgeColor: new THREE.Uniform(new THREE.Color('#8fb4ff')),
        uIntensity: new THREE.Uniform(0),
        uTime: new THREE.Uniform(0),
      },
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false, // 天体の球体にコア部分が削られないように
    })

    this.mesh = new THREE.Mesh(geometry, this.material)
    this.mesh.renderOrder = 2 // ハロー(1)より後に加算描画
    this.mesh.visible = false // uIntensity = 0 の間は描画自体をスキップ
    this.scene.add(this.mesh)

    this.setLensFlare()
  }

  // スクリーンスペースのレンズフレア（フルスクリーンクアッド）
  setLensFlare()
  {
    const geometry = new THREE.PlaneGeometry(2, 2)

    this.flareMaterial = new THREE.ShaderMaterial({
      vertexShader: flareVertexShader,
      fragmentShader: flareFragmentShader,
      uniforms: {
        uLightPos: new THREE.Uniform(new THREE.Vector2(0, 0)),
        uAspect: new THREE.Uniform(this.sizes.width / this.sizes.height),
        uIntensity: new THREE.Uniform(0),
        uVisibility: new THREE.Uniform(1),
        uStrength: new THREE.Uniform(1),
        uTime: new THREE.Uniform(0),
      },
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
    })

    this.flareMesh = new THREE.Mesh(geometry, this.flareMaterial)
    this.flareMesh.frustumCulled = false // NDC 直書きなのでカリング対象外にする
    this.flareMesh.renderOrder = 4 // すべてのエフェクトの最後に加算描画
    this.flareMesh.visible = false
    this.scene.add(this.flareMesh)
  }

  fadeIn()
  {
    this.mesh.visible = true
    this.flareMesh.visible = true
    for (const material of [this.material, this.flareMaterial]) {
      gsap.to(material.uniforms.uIntensity, {
        value: 1.0,
        duration: Math.max(2.5 * this.quality.motionScale, 0.01),
        ease: 'power2.inOut',
        overwrite: true,
      })
    }
  }

  fadeOut()
  {
    gsap.to(this.material.uniforms.uIntensity, {
      value: 0,
      duration: Math.max(2.5 * this.quality.motionScale, 0.01),
      ease: 'power2.inOut',
      overwrite: true,
      onComplete: () => { this.mesh.visible = false },
    })
    gsap.to(this.flareMaterial.uniforms.uIntensity, {
      value: 0,
      duration: Math.max(2.5 * this.quality.motionScale, 0.01),
      ease: 'power2.inOut',
      overwrite: true,
      onComplete: () => { this.flareMesh.visible = false },
    })
  }

  // 追従先（天体のワールド座標）をセット
  updatePosition(worldPosition)
  {
    this.mesh.position.copy(worldPosition)
    this.worldPosition.copy(worldPosition)
  }

  update(elapsed, cameraQuaternion)
  {
    if (!this.mesh.visible) return

    this.material.uniforms.uTime.value = elapsed
    this.mesh.quaternion.copy(cameraQuaternion) // シーン直下なのでそのままカメラ正対

    // フレア: 月のスクリーン座標を投影して渡す
    const ndc = this.scratchVec3.copy(this.worldPosition).project(this.camera.instance)

    // カメラ後方では消し、画面から離れるほど滑らかに減衰させる
    let visibility = ndc.z < 1 ? 1 : 0
    const offCenter = Math.hypot(ndc.x, ndc.y)
    visibility *= 1 - THREE.MathUtils.smoothstep(offCenter, 1.15, 1.6)

    this.flareMaterial.uniforms.uLightPos.value.set(ndc.x, ndc.y)
    this.flareMaterial.uniforms.uVisibility.value = visibility
    this.flareMaterial.uniforms.uAspect.value = this.sizes.width / this.sizes.height
    this.flareMaterial.uniforms.uTime.value = elapsed
  }

  dispose()
  {
    this.scene.remove(this.mesh)
    this.mesh.geometry.dispose()
    this.material.dispose()

    this.scene.remove(this.flareMesh)
    this.flareMesh.geometry.dispose()
    this.flareMaterial.dispose()
  }
}
